import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { useCallback } from 'react'
// Common
import { PopupMenuItem, PopupMenuList } from '$common/popup-menu'
import { RemoveCircleIcon } from '$common/icons/remove-circle'
import { AdministratorIcon } from '$common/icons/administrator'
import { PermissionsIcon } from '$common/icons/permissions'
// Logic
import { useRoom } from '$logic/room/react-hook'

import { removeAdmins, removeMembers } from '$logic/subscription'
// Localization
import { t } from '$i18n'
import {
  openDefineChannelAdminPermissions,
  openDefineGroupAdminPermissions,
  openDefineGroupMemberPermissionsOverride,
  openEditChannelAdminPermissions,
  openEditGroupAdminPermissions,
} from '$logic/router'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import {
  removeAdminFromOpenedUserList,
  removeFromOpenedUserList,
} from '$logic/opened-user-list'
import { getRoom } from '$logic/room'

interface Props {
  isOpen: boolean
  hallId: string
  userId: string
  isAdmin: boolean
  isInAdminsPage?: boolean
}

export function HallMemberMenu({
  isOpen,
  hallId,
  userId,
  isAdmin: otherAdmin,
  isInAdminsPage = false,
}: Props) {
  const hall = useRoom(hallId)
  const isGroup = hall?.type === 'GROUP'
  const permissions = useClientUserPermissions(hallId)
  const meOwner = permissions?.isOwner

  const onEditAdminRights = useCallback(async () => {
    await closeCurrentModal()
    if (isGroup) return openEditGroupAdminPermissions(hallId, userId)
    return openEditChannelAdminPermissions(hallId, userId)
  }, [hallId, isGroup, userId])

  const onPromote = useCallback(async () => {
    await closeCurrentModal()
    if (isGroup) return openDefineGroupAdminPermissions(hallId, userId)
    return openDefineChannelAdminPermissions(hallId, userId)
  }, [hallId, userId, isGroup])

  const onRemoveFromHall = useCallback(async () => {
    const room = await getRoom(hallId)
    analite('CHAT_REMOVE_MEMBER', {
      type: room?.type,
      source: 'HALL_MEMBER_MENU',
    })
    await removeMembers(hallId, [userId])
    removeFromOpenedUserList(userId)
    await closeCurrentModal()
  }, [hallId, userId])

  const onEditUserRights = useCallback(async () => {
    await closeCurrentModal()
    openDefineGroupMemberPermissionsOverride(hallId, userId)
  }, [hallId, userId])

  const onRemoveAdmin = useCallback(async () => {
    await removeAdmins(hallId, [userId])

    if (isInAdminsPage) removeFromOpenedUserList(userId)
    else removeAdminFromOpenedUserList(userId)

    await closeCurrentModal()
  }, [hallId, userId, isInAdminsPage])

  // TODO: CHECK IF OTHER USER IS ADMIN OR NOT
  return (
    <PopupMenuList open={isOpen}>
      {meOwner && otherAdmin && (
        <PopupMenuItem
          text={t('menu.hall.editAdminRights')}
          onClick={onEditAdminRights}
        >
          <AdministratorIcon />
        </PopupMenuItem>
      )}

      {permissions?.addNewAdmins && !otherAdmin && (
        <PopupMenuItem text={t('menu.hall.promote')} onClick={onPromote}>
          <AdministratorIcon />
        </PopupMenuItem>
      )}

      {meOwner && !otherAdmin && isGroup && (
        <PopupMenuItem
          text={t('menu.hall.editRights')}
          onClick={onEditUserRights}
        >
          <PermissionsIcon />
        </PopupMenuItem>
      )}
      {/* TODO : CHECK THIS PERMISSION */}
      {(meOwner || permissions?.banUsers) && !otherAdmin && (
        <PopupMenuItem
          text={
            isGroup
              ? t('menu.hall.removeFromGroup')
              : t('menu.hall.removeFromChannel')
          }
          onClick={onRemoveFromHall}
          error
        >
          <RemoveCircleIcon sx={{ color: 'error.main' }} />
        </PopupMenuItem>
      )}
      {meOwner && otherAdmin && (
        <PopupMenuItem
          text={t('menu.hall.dismissAdmin')}
          onClick={onRemoveAdmin}
          error
        >
          <RemoveCircleIcon sx={{ color: 'error.main' }} />
        </PopupMenuItem>
      )}
    </PopupMenuList>
  )
}
